import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHero, SectionHead, CTABand } from "@/components/PageHero";
import { Reveal, ParallaxImg } from "@/components/anim";
import { IMG } from "@/lib/img";

const STATIONS = [
  {
    id: "makeup",
    title: "Makeup Chair Rental",
    img: IMG.makeupArtist,
    alt: "Makeup artist applying makeup to a model in studio",
    designedFor: [
      "Makeup artists",
      "Lash appointments",
      "Beauty consultations",
      "Glam appointments",
      "Bridal trials",
      "Photoshoot makeup",
    ],
    includes:
      "Includes a professional makeup chair, mirror, lighting area, workspace, and access to shared facilities where applicable.",
    cta: "Book a Makeup Station",
    type: "Makeup Chair",
  },
  {
    id: "hair",
    title: "Hair Chair Rental",
    img: IMG.hairStyling,
    alt: "Stylist working on a client's hair",
    designedFor: [
      "Hairstylists",
      "Braiders",
      "Wig specialists",
      "Natural hair stylists",
      "Styling appointments",
    ],
    includes:
      "Approved services can be carried out from a professional and stylish workspace.",
    cta: "Book a Hair Chair",
    type: "Hair Chair",
  },
  {
    id: "nails",
    title: "Nail Station Rental",
    img: IMG.nailSalon,
    alt: "Nail professional doing a client's nails",
    designedFor: [
      "Independent nail professionals",
      "Manicures",
      "Approved nail services",
    ],
    includes:
      "A polished, professional place to meet your clients — suitable for manicures and approved nail services.",
    cta: "Book a Nail Station",
    type: "Nail Station",
  },
];

const LENGTHS = ["Daily Rentals", "Weekly Rentals", "Monthly Rentals", "Beauty Flex Passes"];

function StationBlock({ s, flip }) {
  return (
    <div
      className="grid grid-cols-1 items-center gap-12 border-t border-beige/60 py-16 lg:grid-cols-2 lg:gap-20"
      data-testid={`beauty-section-${s.id}`}
    >
      <ParallaxImg
        src={s.img}
        alt={s.alt}
        className={`aspect-[4/5] w-full rounded-3xl shadow-[0_30px_80px_rgba(26,24,23,0.14)] ${
          flip ? "lg:order-2" : ""
        }`}
      />
      <div className={flip ? "lg:order-1" : ""}>
        <p className="font-serif text-sm italic text-sanddeep">For beauty professionals</p>
        <h3 className="mt-4 font-serif text-3xl uppercase text-ink sm:text-4xl lg:text-5xl">
          {s.title}
        </h3>
        <p className="mt-6 text-[11px] uppercase tracking-[0.26em] text-fog">Designed for</p>
        <ul className="mt-3 flex flex-wrap gap-2.5">
          {s.designedFor.map((d) => (
            <li key={d} className="chip">
              {d}
            </li>
          ))}
        </ul>
        <p className="mt-6 max-w-lg text-sm leading-relaxed text-clay sm:text-base">
          {s.includes}
        </p>
        <Link
          to={`/book?type=${encodeURIComponent(s.type)}`}
          data-testid={`beauty-book-${s.id}`}
          className="btn btn-dark mt-8"
        >
          {s.cta}
          <ArrowUpRight size={14} strokeWidth={1.5} />
        </Link>
      </div>
    </div>
  );
}

export default function BeautyRentals() {
  return (
    <>
      <PageHero
        eyebrow="Beauty Chair Rentals"
        lines={[
          <>Your Clients. Your Brand.</>,
          <>
            <em>Your Space.</em>
          </>,
        ]}
        sub="Professional beauty workstations without the overhead of your own salon. Independent beauty professionals can rent professional stations by the day, week, or month."
      />

      <section className="bg-paper pb-10" data-testid="beauty-stations">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          {STATIONS.map((s, i) => (
            <StationBlock key={s.id} s={s} flip={i % 2 === 1} />
          ))}
        </div>
      </section>

      <section className="bg-sand py-20 sm:py-28" data-testid="beauty-lengths">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <SectionHead
            eyebrow="Rental lengths"
            title={
              <>
                Rent it
                <br />
                <em>your way.</em>
              </>
            }
          />
          <div className="mt-12 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {LENGTHS.map((l, i) => (
              <Reveal key={l} delay={i * 0.06}>
                <div className="rounded-3xl border border-beige/70 bg-white/80 px-6 py-10 text-center">
                  <p className="font-serif text-4xl italic text-sanddeep">0{i + 1}</p>
                  <p className="mt-4 text-sm uppercase tracking-[0.18em] text-ink">{l}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-paper py-24 sm:py-32" data-testid="beauty-flex-pass">
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2 lg:gap-20 lg:px-10">
          <ParallaxImg
            src={IMG.beautyChairs}
            alt="Modern beauty studio chairs and mirrors"
            className="aspect-[4/3] w-full rounded-3xl shadow-[0_30px_80px_rgba(26,24,23,0.12)]"
          />
          <div>
            <SectionHead
              eyebrow="Beauty Flex Pass"
              title={
                <>
                  No permanent chair?
                  <br />
                  <em>No problem.</em>
                </>
              }
              sub="A flexible option for professionals who do not require a permanent chair. Purchase a set number of rental days each month."
            />
            <Reveal delay={0.3}>
              <div className="mt-8 rounded-3xl border border-blushdeep bg-blush/60 p-8" data-testid="flex-pass-card">
                <p className="font-serif text-3xl uppercase text-ink">
                  4-Day <em>Flex Pass</em>
                </p>
                <p className="mt-3 text-sm leading-relaxed text-clay">
                  Choose four available working days each month — ideal for professionals
                  transitioning from mobile or home services into a professional physical
                  environment.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand
        title={
          <>
            Bring your clients. Build your brand.
            <br />
            <em>We provide the space.</em>
          </>
        }
        cta="Book a Beauty Station"
        to="/book?type=Makeup%20Chair"
        testid="beauty-cta-button"
      />
    </>
  );
}
